
///
/// EarthBrowser
///
/// Created by Matt Giger on 9/2/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Constants } from '@/constants'
import { combineReducers, configureStore, createListenerMiddleware } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE, type PersistedState } from "redux-persist"
import storage from "redux-persist/lib/storage"
import { authenticatedApi } from './API/authenticatedApi'
import type { DatasetBrowserWindowState } from './dialogSlice'
import { dialogSlice } from './dialogSlice'
import { earthBrowserSlice } from './earthBrowserSlice'
import { engagementSlice } from './engagementSlice'
import { entitlementsSlice } from './entitlementsSlice'
import featureFlagsReducer from './FeatureFlags'
import gitConfigReducer from './gitConfigSlice'
import { migrations, runMigrations } from './migrations'
import datasetSceneReducer from './datasetSceneSlice'
import { ogcServersSlice } from './ogcServersSlice'
import { addLocations, deletePlacemark, editPlacemark, placemarksSlice, removeLocation, removePlacemarkTombstones, upsertPlacemark } from './placemarksSlice'
import { placemarksApi, placemarkToPutRequest } from './placemarksApi'
import { savedViewsApi, viewToPutRequest } from './savedViewsApi'
import { deleteView, removeTombstones, renameView, savedViewsSlice, setViewTags, upsertView } from './savedViewsSlice'
import { shaderSourceSlice } from './shaderSourceSlice'
import {
	addStop,
	deleteAnnotation,
	deleteStop,
	deleteStory,
	moveStop,
	recaptureStop,
	removeStoryTombstones,
	renameStory,
	setStoryTags,
	storiesSlice,
	updateStop,
	upsertAnnotation,
	upsertStory,
} from './storiesSlice'
import { storiesApi, storyToPutRequest } from './storiesApi'
import {
	addDockedWindowId,
	removeDockedWindowId,
	removeWindowState,
	saveWindowState,
	setPersistedBottomDrawerActiveTab,
	setPersistedBottomDrawerHeight,
	setPersistedBottomDrawerIsOpen,
	userInterfaceSlice,
	type PersistedWindowState,
} from './userInterfaceSlice'
import { userSlice } from './userSlice'
import { analyticsMiddleware } from './analyticsMiddleware'
import { memoryGateMiddleware } from './memoryGateMiddleware'
import { textureUnitGateMiddleware } from './textureUnitGateMiddleware'
import { validationMiddleware } from './validation/validationMiddleware'

const cStorageKey = `earthbrowser_${Constants.persistStorageVersion}`
const CURRENT_VERSION = migrations.length

// Create a fallback storage that uses in-memory storage when localStorage fails
// This prevents redux-persist from disabling persistence entirely
function createFallbackStorage() {
	// In-memory storage as fallback
	const memoryStorage: Record<string, string> = {}

	const fallbackStorage = {
		getItem: async (key: string): Promise<string | null> => {
			return memoryStorage[key] || null
		},
		setItem: async (key: string, value: string): Promise<void> => {
			memoryStorage[key] = value
		},
		removeItem: async (key: string): Promise<void> => {
			delete memoryStorage[key]
		}
	}

	// Check if we're in a browser environment
	if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
		console.warn('[Redux Persist] Running in server environment, using in-memory storage')
		return fallbackStorage
	}

	// Try remediation strategies when localStorage fails
	function tryRemediationStrategies(): boolean {
		try {

			// Strategy 1: Clear old versions of this app's data
			const oldKeys: string[] = []
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i)
				if (key && key.startsWith('earthbrowser_') && key !== cStorageKey) {
					oldKeys.push(key)
				}
			}

			if (oldKeys.length > 0) {
				oldKeys.forEach(key => localStorage.removeItem(key))
			}

			// Strategy 2: Clear other potentially stale data
			const stalePatterns = [
				'persist:',  // Old redux-persist data
				'redux-',    // Old redux data
				'__',        // Test/debug keys
			]

			const staleKeys: string[] = []
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i)
				if (key && key !== cStorageKey) {
					// Check if key matches stale patterns and is old
					for (const pattern of stalePatterns) {
						if (key.startsWith(pattern)) {
							staleKeys.push(key)
							break
						}
					}
				}
			}

			if (staleKeys.length > 0) {
				staleKeys.forEach(key => localStorage.removeItem(key))
			}

			// Test if localStorage works now
			const testKey = '__redux_persist_test__'
			localStorage.setItem(testKey, 'test')
			localStorage.removeItem(testKey)

			return true
		} catch (e) {
			console.warn('[Redux Persist] Remediation failed:', e)
			return false
		}
	}

	// Try to use localStorage
	try {
		const testKey = '__redux_persist_test__'
		localStorage.setItem(testKey, 'test')
		localStorage.removeItem(testKey)
		return storage
	} catch (e) {
		console.warn('[Redux Persist] Initial localStorage test failed:', e)

		// Try remediation strategies
		const remediated = tryRemediationStrategies()

		if (remediated) {
			return storage
		} else {
			return fallbackStorage
		}
	}
}

const persistStorage = createFallbackStorage()

const rootReducer = combineReducers({
	user: userSlice.reducer,
	shadersource: shaderSourceSlice.reducer,
	dialog: dialogSlice.reducer,
	earthbrowser: earthBrowserSlice.reducer,
	featureFlags: featureFlagsReducer,
	ogcServers: ogcServersSlice.reducer,
	datasetScene: datasetSceneReducer,
	userInterface: userInterfaceSlice.reducer,
	gitConfig: gitConfigReducer,
	savedViews: savedViewsSlice.reducer,
	placemarks: placemarksSlice.reducer,
	stories: storiesSlice.reducer,
	engagement: engagementSlice.reducer,
	entitlements: entitlementsSlice.reducer,
	[authenticatedApi.reducerPath]: authenticatedApi.reducer,
})

function createStore() {
	const listenerMiddleware = createListenerMiddleware()

	// Add listener for window state persistence
	listenerMiddleware.startListening({
		predicate: (action) => {
			// Listen to window-related actions
			return action.type.startsWith('dialog/openDatasetWindow') ||
				   action.type.startsWith('dialog/updateDatasetWindow') ||
				   action.type.startsWith('dialog/minimizeDatasetWindow') ||
				   action.type.startsWith('dialog/restoreDatasetWindow') ||
				   action.type.startsWith('dialog/toggleMaximizeDatasetWindow') ||
				   action.type.startsWith('dialog/closeDatasetWindow') ||
				   action.type.startsWith('dialog/dockWindowToDrawer') ||
				   action.type.startsWith('dialog/undockTabToWindow') ||
				   action.type.startsWith('dialog/closeDrawerTab')
		},
		effect: (action, listenerApi) => {
			const state = listenerApi.getState() as RootState

			// Helper to convert window state to persisted format
			const toPersistedWindowState = (window: DatasetBrowserWindowState): PersistedWindowState => ({
				id: window.id,
				x: window.x,
				y: window.y,
				width: window.width,
				height: window.height,
				isMaximized: window.isMaximized,
				isVisible: window.isVisible,
				title: window.title,
				windowType: window.windowType,
				dataSourceId: window.dataSourceId,
				catalogId: window.catalogId,
				activeLayerName: window.activeLayerName,
				status: window.status,
				dockMode: window.dockMode,
				isDockedInDrawer: window.isDockedInDrawer,
			})

			if (action.type === 'dialog/closeDatasetWindow' || action.type === 'dialog/closeDrawerTab') {
				// Remove from persisted storage
				const windowId = (action as { type: string; payload: string }).payload
				listenerApi.dispatch(removeWindowState(windowId))
			} else if (action.type === 'dialog/openDatasetWindow') {
				// Save new window
				const windowId = (action as { type: string; payload: { id: string } }).payload.id
				const window = state.dialog.datasetWindows[windowId]
				if (window) {
					listenerApi.dispatch(saveWindowState(toPersistedWindowState(window)))
				}
			} else if (action.type === 'dialog/updateDatasetWindow' ||
					   action.type === 'dialog/minimizeDatasetWindow' ||
					   action.type === 'dialog/restoreDatasetWindow' ||
					   action.type === 'dialog/toggleMaximizeDatasetWindow' ||
					   action.type === 'dialog/dockWindowToDrawer' ||
					   action.type === 'dialog/undockTabToWindow') {
				// Update existing window
				const payload = (action as { payload?: string | { id?: string; windowId?: string } }).payload
				const payloadObject = typeof payload === 'object' && payload !== null ? payload : undefined
				const windowId = payloadObject?.id || payloadObject?.windowId || (typeof payload === 'string' ? payload : undefined)
				const window = windowId ? state.dialog.datasetWindows[windowId] : undefined
				if (window) {
					listenerApi.dispatch(saveWindowState(toPersistedWindowState(window)))
				}
			}
		}
	})

	// Live push of saved-view mutations to the server when signed in.
	// Failures are fine: the delta sync (savedViewsSync.ts) retries any
	// view whose updatedAt is newer than lastSyncedAt on the next trigger.
	listenerMiddleware.startListening({
		predicate: (action) => {
			return upsertView.match(action) ||
				   renameView.match(action) ||
				   setViewTags.match(action) ||
				   deleteView.match(action)
		},
		effect: (action, listenerApi) => {
			const state = listenerApi.getState() as RootState
			if (!state.user.userProfile) return

			if (deleteView.match(action)) {
				const { id } = action.payload
				void listenerApi.dispatch(savedViewsApi.endpoints.deleteSavedView.initiate(id))
					.unwrap()
					.then(() => listenerApi.dispatch(removeTombstones([id])))
					.catch(() => undefined)
				return
			}

			const { id } = action.payload as { id: string }
			const view = state.savedViews.views[id]
			if (!view) return
			void listenerApi.dispatch(
				savedViewsApi.endpoints.putSavedView.initiate({ id: view.id, body: viewToPutRequest(view) })
			).unwrap().catch(() => undefined)
		}
	})

	// Live push of placemark mutations to the server when signed in.
	// Failures are fine: the delta sync (placemarksSync.ts) retries any
	// placemark whose updatedAt is newer than lastSyncedAt on the next
	// trigger.
	listenerMiddleware.startListening({
		predicate: (action) => {
			return upsertPlacemark.match(action) ||
				   editPlacemark.match(action) ||
				   addLocations.match(action) ||
				   removeLocation.match(action) ||
				   deletePlacemark.match(action)
		},
		effect: (action, listenerApi) => {
			const state = listenerApi.getState() as RootState
			if (!state.user.userProfile) return

			if (deletePlacemark.match(action)) {
				const { id } = action.payload
				void listenerApi.dispatch(placemarksApi.endpoints.deletePlacemark.initiate(id))
					.unwrap()
					.then(() => listenerApi.dispatch(removePlacemarkTombstones([id])))
					.catch(() => undefined)
				return
			}

			const { id } = action.payload as { id: string }
			const placemark = state.placemarks.placemarks[id]
			if (!placemark) return
			void listenerApi.dispatch(
				placemarksApi.endpoints.putPlacemark.initiate({ id: placemark.id, body: placemarkToPutRequest(placemark) })
			).unwrap().catch(() => undefined)
		}
	})

	// Live push of story mutations to the server when signed in.
	// Failures are fine: the delta sync (storiesSync.ts) retries any
	// story whose updatedAt is newer than lastSyncedAt on the next
	// trigger.
	listenerMiddleware.startListening({
		predicate: (action) => {
			return upsertStory.match(action) ||
				   renameStory.match(action) ||
				   setStoryTags.match(action) ||
				   addStop.match(action) ||
				   updateStop.match(action) ||
				   recaptureStop.match(action) ||
				   moveStop.match(action) ||
				   deleteStop.match(action) ||
				   upsertAnnotation.match(action) ||
				   deleteAnnotation.match(action) ||
				   deleteStory.match(action)
		},
		effect: (action, listenerApi) => {
			const state = listenerApi.getState() as RootState
			if (!state.user.userProfile) return

			if (deleteStory.match(action)) {
				const { id } = action.payload
				void listenerApi.dispatch(storiesApi.endpoints.deleteStoryRemote.initiate(id))
					.unwrap()
					.then(() => listenerApi.dispatch(removeStoryTombstones([id])))
					.catch(() => undefined)
				return
			}

			const { id } = action.payload as { id: string }
			const story = state.stories.stories[id]
			if (!story) return
			void listenerApi.dispatch(
				storiesApi.endpoints.putStory.initiate({ id: story.id, body: storyToPutRequest(story) })
			).unwrap().catch(() => undefined)
		}
	})

	// Add listener for bottom drawer state persistence
	listenerMiddleware.startListening({
		predicate: (action) => {
			return action.type.startsWith('dialog/dockWindowToDrawer') ||
				   action.type.startsWith('dialog/undockTabToWindow') ||
				   action.type.startsWith('dialog/closeDrawerTab') ||
				   action.type.startsWith('dialog/setActiveDrawerTab') ||
				   action.type.startsWith('dialog/setDrawerHeight') ||
				   action.type.startsWith('dialog/toggleDrawer') ||
				   action.type.startsWith('dialog/openDrawer') ||
				   action.type.startsWith('dialog/closeDrawer')
		},
		effect: (action, listenerApi) => {
			const state = listenerApi.getState() as RootState

			// Sync drawer height
			if (action.type === 'dialog/setDrawerHeight') {
				listenerApi.dispatch(setPersistedBottomDrawerHeight(state.dialog.bottomDrawer.height))
			}

			// Sync dock open state (dock/undock/openDrawer can change it too,
			// not just the explicit toggle/close)
			const isOpen = state.dialog.bottomDrawer.isOpen
			if (isOpen !== state.userInterface.bottomDrawer.isOpen) {
				listenerApi.dispatch(setPersistedBottomDrawerIsOpen(isOpen))
			}

			// Sync docked window IDs
			if (action.type === 'dialog/dockWindowToDrawer') {
				const payload = (action as { payload?: string | { windowId?: string } }).payload
				const windowId = typeof payload === 'string' ? payload : payload?.windowId
				if (windowId) {
					listenerApi.dispatch(addDockedWindowId(windowId))
				}
			} else if (action.type === 'dialog/undockTabToWindow') {
				const windowId = (action as { payload?: { windowId?: string } }).payload?.windowId
				if (windowId) {
					listenerApi.dispatch(removeDockedWindowId(windowId))
				}
			} else if (action.type === 'dialog/closeDrawerTab') {
				const windowId = (action as { type: string; payload: string }).payload
				listenerApi.dispatch(removeDockedWindowId(windowId))
			}

			// Sync active tab (dock/undock/close can change it too, not just setActiveDrawerTab)
			const activeTabId = state.dialog.bottomDrawer.activeTabId
			if (activeTabId !== state.userInterface.bottomDrawer.activeTabId) {
				listenerApi.dispatch(setPersistedBottomDrawerActiveTab(activeTabId))
			}
		}
	})

	if(typeof window !== 'undefined') {

		const persistedReducer = persistReducer(
			{
				key: cStorageKey,
				version: CURRENT_VERSION,
				storage: persistStorage,
				blacklist: [
					dialogSlice.reducerPath,
					shaderSourceSlice.reducerPath,
					'ogcSync',
					'ogcScene',
					'datasetScene',
					// Entitlements are re-resolved from getUserSubscription on
					// every load; persisting would leak paid depth to free users
					entitlementsSlice.reducerPath,
					authenticatedApi.reducerPath,
				],
				debug: true,
				// NOTE: redux-persist passes the CONFIG version (target) as the second
				// argument, not the stored version. The stored version must be read
				// from state._persist.version or migrations silently never run.
				migrate: (state: PersistedState) => {
					const storedVersion: number = state?._persist?.version ?? -1
					const migratedState = runMigrations(state, storedVersion, CURRENT_VERSION) as PersistedState
					return Promise.resolve(migratedState)
				}
			},
			rootReducer
		)

		return configureStore({
			reducer: persistedReducer,
			middleware: (getDefaultMiddleware) => {
				return getDefaultMiddleware({
					immutableCheck: { warnAfter: 128 },
					serializableCheck: {
						warnAfter: 128,
						ignoredActions: [
							FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER,
							'ogcSync/setSyncService',
							'ogcScene/setSceneManager',
							'datasetScene/setLayerManager',
						],
						ignoredPaths: [
							'ogcSync.syncService',
							'ogcScene.sceneManager',
							'datasetScene.layerManager',
						],
					},
				})
				.prepend(listenerMiddleware.middleware)
				.concat(authenticatedApi.middleware)
				.concat(memoryGateMiddleware)
				.concat(textureUnitGateMiddleware)
				// After the gates so blocked enables aren't counted
				.concat(analyticsMiddleware)
				.concat(validationMiddleware)
			}
		})
	} else {
		return configureStore({
			reducer: rootReducer,
			middleware: (getDefaultMiddleware) => {
				return getDefaultMiddleware({
					immutableCheck: { warnAfter: 128 },
					serializableCheck: {
						warnAfter: 128,
						ignoredActions: [
							FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER,
							'ogcSync/setSyncService',
							'ogcScene/setSceneManager',
							'datasetScene/setLayerManager',
						],
						ignoredPaths: [
							'ogcSync.syncService',
							'ogcScene.sceneManager',
							'datasetScene.layerManager',
						],
					},
				})
				.prepend(listenerMiddleware.middleware)
				.concat(authenticatedApi.middleware)
				.concat(analyticsMiddleware)
				.concat(validationMiddleware)
			}
		})
	}
}

export const appStore = createStore()
setupListeners(appStore.dispatch)

// Note: State validation happens in StoreProvider's onBeforeLift
export const persistor = persistStore(appStore)

export type AppStore = typeof appStore
export type RootState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()
export const useAppStore = useStore.withTypes<AppStore>()